import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2, CheckCircle2, ArrowLeft, Upload } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { brl, dateBR } from "../lib/format";
import { Pill } from "./common";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const ROW = {
  ok: ["Pronto", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  duplicado: ["Duplicado · ignorado", "bg-slate-100 text-slate-600 border-slate-200"],
  erro: ["Com erro", "bg-rose-50 text-rose-700 border-rose-200"],
};
const fmtCpf = (c) => (c && c.length === 11 ? `${c.slice(0, 3)}.${c.slice(3, 6)}.${c.slice(6, 9)}-${c.slice(9)}` : c || "—");

function UploadStep({ busy, onFile }) {
  return (
    <label data-testid="import-dropzone" className="flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-xl py-12 px-6 text-center cursor-pointer hover:border-sky-400 hover:bg-sky-50/40 transition-colors">
      {busy ? <Loader2 className="h-8 w-8 animate-spin text-sky-600" /> : <FileSpreadsheet className="h-8 w-8 text-sky-600" />}
      <div className="font-medium">Selecione a planilha de funcionários (.xlsx ou .csv)</div>
      <div className="text-xs text-slate-500 max-w-md">Pode ser a planilha que o cliente já usa: a primeira linha deve ter os títulos das colunas. Nós sugerimos as colunas automaticamente.</div>
      <input data-testid="import-file-input" type="file" accept=".xlsx,.csv" className="hidden" disabled={busy} onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
    </label>
  );
}

function MapStep({ pv, mapping, setMapping }) {
  const found = Object.values(pv.mapping).filter((v) => v !== null).length;
  return (
    <div className="space-y-3">
      <div className="text-sm text-slate-600" data-testid="import-map-summary"><b>{pv.total_rows}</b> linha(s) em <b>{pv.filename}</b> · reconhecemos <b>{found}</b> coluna(s) automaticamente. Confira e ajuste se precisar.</div>
      <div className="grid sm:grid-cols-2 gap-2 max-h-[52vh] overflow-y-auto pr-1">
        {pv.fields.map((f) => {
          const v = mapping[f.key];
          const sample = v !== null && v !== undefined ? pv.sample.map((r) => r[v]).find((x) => x !== null && x !== "") : null;
          return (
            <div key={f.key} className="border rounded-lg px-3 py-2">
              <div className="text-xs font-medium text-slate-700">{f.label}{f.required && <span className="text-rose-600"> *</span>}</div>
              <Select value={v === null || v === undefined ? "none" : String(v)} onValueChange={(x) => setMapping({ ...mapping, [f.key]: x === "none" ? null : Number(x) })}>
                <SelectTrigger data-testid={`import-map-${f.key}`} className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">— Não importar —</SelectItem>{pv.headers.map((h, i) => <SelectItem key={i} value={String(i)}>{h}</SelectItem>)}</SelectContent>
              </Select>
              <div className="text-[11px] text-slate-400 mt-1 truncate">Ex.: {sample ?? "—"}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CheckStep({ res }) {
  const [filter, setFilter] = useState("all");
  const s = res.summary;
  const rows = res.rows.filter((r) => filter === "all" || (filter === "warn" ? r.status === "ok" && r.warnings.length : r.status === filter));
  const chips = [["all", `Todas (${s.total})`], ["ok", `Prontos (${s.ok})`], ["warn", `Com avisos (${s.warnings})`], ["duplicado", `Duplicados (${s.duplicates})`], ["erro", `Com erro (${s.errors})`]];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">{chips.map(([k, l]) => <button key={k} data-testid={`import-filter-${k}`} onClick={() => setFilter(k)} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white hover:bg-slate-50"}`}>{l}</button>)}</div>
      <div className="border rounded-lg max-h-[48vh] overflow-auto">
        <table className="w-full text-xs" data-testid="import-check-table">
          <thead className="bg-slate-50 text-slate-500 sticky top-0"><tr>{["Linha", "Nome", "CPF", "Cargo", "Departamento", "Salário", "Admissão", "Situação"].map((h) => <th key={h} className="text-left font-medium px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.line} className="border-t align-top" data-testid={`import-row-${r.line}`}>
              <td className="px-3 py-2 font-mono text-slate-400">{r.line}</td><td className="px-3 py-2 font-medium">{r.data.name || "—"}</td>
              <td className="px-3 py-2 font-mono">{fmtCpf(r.data.cpf)}</td><td className="px-3 py-2">{r.data.position || "—"}</td><td className="px-3 py-2">{r.data.department || "—"}</td>
              <td className="px-3 py-2 font-mono">{r.data.salary ? brl(r.data.salary, 2) : "—"}</td><td className="px-3 py-2 font-mono">{r.data.admission_date ? dateBR(r.data.admission_date) : "—"}</td>
              <td className="px-3 py-2"><Pill cls={ROW[r.status][1]}>{ROW[r.status][0]}</Pill>
                {[...r.errors, ...r.warnings].map((m) => <div key={m} className={`mt-1 ${r.errors.includes(m) ? "text-rose-700" : "text-amber-700"}`}>{m}</div>)}</td>
            </tr>))}</tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500">Duplicados (CPF já cadastrado ou repetido) e linhas com erro não serão gravados. Avisos não impedem a importação.</p>
    </div>
  );
}

export function EmployeeImportDialog({ open, onOpenChange, companyId, onDone }) {
  const [step, setStep] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [pv, setPv] = useState(null);
  const [mapping, setMapping] = useState({});
  const [res, setRes] = useState(null);
  const [done, setDone] = useState(null);
  const params = companyId ? { params: { company_id: companyId } } : {};
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const upload = (file) => run(async () => { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/employee-imports/preview", fd, params); setPv(data); setMapping(data.mapping); setStep("map"); });
  const check = () => run(async () => { const { data } = await api.post(`/employee-imports/${pv.id}/check`, { mapping }, params); setRes(data); setStep("check"); });
  const confirm = () => run(async () => { const { data } = await api.post(`/employee-imports/${pv.id}/confirm`, {}, params); setDone(data); setStep("done"); onDone?.(); });
  const close = (o) => { if (!o) { setStep("upload"); setPv(null); setRes(null); setDone(null); } onOpenChange(o); };
  const titles = { upload: "Importar funcionários", map: "1. Confira as colunas", check: "2. Confira os dados antes de gravar", done: "Importação concluída" };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-4xl" data-testid="employee-import-dialog">
        <DialogHeader><DialogTitle>{titles[step]}</DialogTitle><DialogDescription>Traga todos os funcionários de um cliente novo de uma vez. Nada é gravado antes da sua confirmação.</DialogDescription></DialogHeader>
        {step === "upload" && <UploadStep busy={busy} onFile={upload} />}
        {step === "map" && <MapStep pv={pv} mapping={mapping} setMapping={setMapping} />}
        {step === "check" && <CheckStep res={res} />}
        {step === "done" && (
          <div className="flex flex-col items-center text-center gap-2 py-8" data-testid="import-done">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
            <div className="font-display text-2xl font-semibold" data-testid="import-done-count">{done.imported} funcionário(s) importado(s)</div>
            <div className="text-sm text-slate-500">{done.duplicates} duplicado(s) ignorado(s) · {done.errors} linha(s) com erro não importada(s)</div>
          </div>
        )}
        <DialogFooter className="gap-2">
          {step === "map" && <><Button variant="outline" onClick={() => setStep("upload")}><ArrowLeft className="h-4 w-4 mr-1.5" />Outra planilha</Button>
            <Button data-testid="import-check-button" disabled={busy || mapping.name == null || mapping.cpf == null} onClick={check}>{busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Conferir dados</Button></>}
          {step === "check" && <><Button variant="outline" data-testid="import-back-to-map" onClick={() => setStep("map")}><ArrowLeft className="h-4 w-4 mr-1.5" />Ajustar colunas</Button>
            <Button data-testid="import-confirm-button" disabled={busy || !res.summary.ok} onClick={confirm}>{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Gravar {res.summary.ok} funcionário(s)</Button></>}
          {step === "done" && <Button data-testid="import-close-button" onClick={() => close(false)}>Fechar</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
